import { useState } from "react";
import {
  Title,
  Text,
  Card,
  Group,
  Button,
  TextInput,
  Stack,
  Modal,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { workspaceService } from "../lib/services";

export function WorkspaceSettingsPage() {
  const { activeWorkspace, workspaces, removeWorkspace } = useWorkspace();
  const navigate = useNavigate();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isOwner = activeWorkspace?.role === "owner";
  const isPersonal = activeWorkspace?.type === "personal";
  const workspaceName = activeWorkspace?.name ?? "";
  const canDelete = confirmText === workspaceName;

  async function handleDelete() {
    if (!activeWorkspace || !canDelete) return;
    setDeleting(true);
    try {
      const deletedId = activeWorkspace.id;
      await workspaceService.delete(deletedId);
      notifications.show({
        title: "Workspace deleted",
        message: `"${workspaceName}" has been permanently deleted.`,
        color: "red",
      });
      // Update context locally — no refetch, no loading flash
      const remaining = workspaces.filter((w) => w.id !== deletedId);
      removeWorkspace(deletedId);
      if (remaining.length > 0) {
        localStorage.setItem("showrunner:lastWorkspaceId", remaining[0].id);
        navigate(`/${remaining[0].id}`, { replace: true });
      } else {
        localStorage.removeItem("showrunner:lastWorkspaceId");
        navigate("/", { replace: true });
      }
    } catch (err) {
      notifications.show({
        title: "Failed to delete workspace",
        message: err instanceof Error ? err.message : "Something went wrong",
        color: "red",
      });
    } finally {
      setDeleting(false);
      setDeleteModalOpen(false);
      setConfirmText("");
    }
  }

  return (
    <>
      <Title order={2} mb="xs">
        Workspace Settings
      </Title>
      <Text c="dimmed" mb="xl">
        Manage settings for <strong>{workspaceName}</strong>
      </Text>

      <Stack gap="lg">
        {isOwner && !isPersonal && (
          <Card withBorder p="lg" radius="md" style={{ borderColor: "var(--mantine-color-red-outline)" }}>
            <Stack gap="sm">
              <Title order={4} c="red">
                Danger Zone
              </Title>
              <Text size="sm" c="dimmed">
                Permanently delete this workspace and all of its data including prompts,
                versions, tags, API keys, and SDK tokens. This action cannot be undone.
              </Text>
              <Group>
                <Button
                  color="red"
                  variant="outline"
                  leftSection={<IconTrash size={16} />}
                  onClick={() => setDeleteModalOpen(true)}
                >
                  Delete workspace
                </Button>
              </Group>
            </Stack>
          </Card>
        )}

        {isOwner && isPersonal && (
          <Text c="dimmed" size="sm">
            Your personal workspace cannot be deleted.
          </Text>
        )}

        {!isOwner && (
          <Text c="dimmed">
            Only the workspace owner can manage workspace settings.
          </Text>
        )}
      </Stack>

      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setConfirmText("");
        }}
        title="Delete workspace"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This will permanently delete <strong>{workspaceName}</strong> and all
            its data. This action cannot be undone.
          </Text>
          <TextInput
            label={
              <>
                Type <strong>{workspaceName}</strong> to confirm
              </>
            }
            placeholder={workspaceName}
            value={confirmText}
            onChange={(e) => setConfirmText(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDeleteModalOpen(false);
                setConfirmText("");
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              disabled={!canDelete}
              loading={deleting}
              onClick={handleDelete}
            >
              Delete workspace
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
